import { useState, useEffect } from 'react';

import {
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Stack,
  Radio,
  Box
} from '@material-ui/core';
import PropTypes from 'prop-types';
import GroupIcon from '@mui/icons-material/Group';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import PreviewButtonComponent from '../../team/PreviewButtonComponent';
import SnapshotPreviewModal from '../../team/SnapshotPreviewModal';

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    p: 4
  },
  table: {
    '&': { px: 1 },
    '.MuiDataGrid-cellContent': {
      overflow: 'auto',
      textOverflow: 'inherit'
    }
  }
};

SearchSnapshotModal.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  snapshots: PropTypes.array,
  setSelectedSnapshot: PropTypes.func
};

export default function SearchSnapshotModal({
  isOpen,
  closeHandler,
  snapshots,
  setSelectedSnapshot
}) {
  const [pageSize, setPageSize] = useState(5);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [selectionModel, setSelectionModel] = useState([]);
  const [teamOnly, setTeamOnly] = useState(false);
  const [filteredSnapshots, setFilteredSnapshots] = useState(snapshots);

  const handleToggleTeamSnapshots = () => {
    setTeamOnly((prev) => {
      const next = !prev;
      return next;
    });
  };

  function QuickSearchToolbar() {
    return (
      <Stack direction="row" spacing={1} sx={{ mx: 1.0, mt: 2 }}>
        <GridToolbarQuickFilter
          variant="outlined"
          size="small"
          quickFilterParser={(searchInput) =>
            searchInput
              .split(',')
              .map((value) => value.trim())
              .filter((value) => value !== '')
          }
        />
        <Button
          startIcon={<GroupIcon />}
          variant={teamOnly ? 'contained' : 'outlined'} // 🔥 visual toggle
          color={teamOnly ? 'primary' : 'inherit'}
          onClick={handleToggleTeamSnapshots}
          sx={{ height: 40 }}
        >
          Team Snapshots Only
        </Button>
      </Stack>
    );
  }

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Radio
          size="small"
          checked={selectionModel[0] === params.id}
          onChange={() => setSelectionModel([params.id])}
        />
      )
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.5
    },
    {
      field: 'baseline_type',
      headerName: 'Type',
      flex: 0.5,
      valueGetter: (params) =>
        params.row.name.split('-')[0].includes('pk') ? 'Baseline PACOS' : '',
      renderCell: (params) => (params.value ? params.value : 'custom')
    },
    {
      field: 'system_type',
      headerName: 'System Type',
      flex: 0.5,
      valueGetter: (params) => params.row.system_type || 'ife'
    },
    {
      field: 'provisioner_identity',
      headerName: 'Team Identity',
      flex: 0.5
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      renderCell: (params) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            cursor: 'pointer'
          }}
          title={params.value} // tooltip on hover
        >
          {params.value}
        </div>
      )
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.5,
      renderCell: (param) => (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <PreviewButtonComponent
            type="icononly"
            variant="contained"
            data={param.row}
            tooltipTitle="Preview Snapshot"
            ModalComponent={SnapshotPreviewModal}
            modalProps={{
              data: param.row
            }}
          />
        </Box>
      )
    }
  ];

  const updatePageSize = (pageSize) => {
    setPageSize(pageSize);
  };

  const handleSelectSnapshot = () => {
    const selectedId = selectionModel[0];
    const selected = snapshots.find((row) => row.name === selectedId);

    if (selected) {
      setSelectedSnapshot(selected);
      closeHandler();
    }
  };

  useEffect(() => {
    setFilteredSnapshots(teamOnly ? snapshots.filter((s) => s.global === false) : snapshots);
  }, [teamOnly, snapshots]);

  return (
    <Modal
      open={isOpen}
      sx={{ outline: 'none' }}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <Grid container sx={styles.modal}>
          <Grid item xs={12} md={1} onClick={closeHandler} />

          <Grid item xs={12} md={10}>
            <Card>
              <CardHeader title="Search Snapshots" />
              <Divider />

              <CardContent>
                <DataGrid
                  disableVirtualization
                  rows={filteredSnapshots}
                  columns={columns}
                  getRowId={(row) => row.name}
                  autoHeight
                  pagination
                  pageSize={pageSize}
                  onPageSizeChange={updatePageSize}
                  rowsPerPageOptions={[5, 10, 20, 50, 100]}
                  selectionModel={selectionModel}
                  onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                  disableMultipleSelection
                  disableSelectionOnClick={false}
                  filterModel={filterModel}
                  onFilterModelChange={(model) => setFilterModel(model)}
                  components={{ Toolbar: QuickSearchToolbar }}
                  sx={{
                    ...styles.table,
                    '& .Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                />
              </CardContent>

              <Divider />

              <Stack direction="row" justifyContent="flex-end" p={1}>
                <Button variant="outlined" onClick={closeHandler}>
                  Close
                </Button>

                <Button
                  sx={{ ml: 1 }}
                  variant="contained"
                  disabled={!selectionModel.length}
                  onClick={handleSelectSnapshot}
                >
                  Select Snapshot
                </Button>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={1} onClick={closeHandler} />
        </Grid>
      </Fade>
    </Modal>
  );
}
